import * as FileSaver from "file-saver";
import * as XLSX from "sheetjs-style";

import { RoyaltyReportLine } from "../interface/api/RoyaltyReportLine";
import { SkippedTicket } from "../interface/api/SkippedTicket";
import { SkippedTicketReason, royaltyStreamLabel } from "./constants";
import { formatDate } from "./date-helper";

const XLSX_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const priceSource = (line: RoyaltyReportLine) => {
  if (line.netTicketPrice != null) return "Net Ticket Price";
  if (line.estimatedSalePrice) return "Estimated Average";
  return line.missingSalePrice ? "No Sale Price" : "Actual Price";
};

function save(
  headers: string[],
  rows: (string | number)[][],
  fileName: string,
) {
  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  sheet["!cols"] = headers.map((header, column) => {
    const widest = rows.reduce(
      (max, row) => Math.max(max, String(row[column] ?? "").length),
      header.length,
    );
    return { wch: widest + 2 };
  });

  const buffer = XLSX.write(
    { Sheets: { rows: sheet }, SheetNames: ["rows"] },
    { bookType: "xlsx", type: "array", cellStyles: true },
  );

  FileSaver.saveAs(new Blob([buffer], { type: XLSX_TYPE }), `${fileName}.xlsx`);
}

/**
 * The lines on screen, as a workbook. The emailed report is the whole period; this is
 * whatever the filter is showing — usually the unpriced ones somebody is about to go and
 * back-fill, which is not a list worth re-deriving from a 10,000-row file by hand.
 */
export const exportRoyaltyLines = (
  lines: RoyaltyReportLine[],
  fileName: string,
  grouped = false,
  showNetPrice = false,
) => {
  // Same columns and price-source wording as the emailed workbook (RoyaltyController), so a
  // filtered export reads like a slice of the report rather than a different document. The
  // Stream column stands in for the workbook's group headings on an All Streams run.

  const headers = [
    ...(grouped ? ["Stream"] : []),
    "Ticket #",
    "Date",
    "Customer",
    "Source / Job Site",
    "Sale Price",
    ...(showNetPrice ? ["Net Ticket Price"] : []),
    "Adjusted Ticket Price",
    "Difference",
    "Net Royalty",
    "Price Source",
  ];

  const rows = lines.map((line) => [
    ...(grouped ? [royaltyStreamLabel(line.stream)] : []),
    line.ticketNumber,
    line.date ? formatDate(line.date, false) : "",
    line.customer ?? "",
    line.jobSite ?? "",
    // A net-priced line shows its recorded sale price for reference, as the workbook does.
    line.netTicketPrice != null ? line.salePrice ?? "" : line.effectiveSalePrice,
    ...(showNetPrice ? [line.netTicketPrice ?? ""] : []),
    line.adjustedTicketPrice,
    line.difference,
    line.royalty,
    priceSource(line),
  ]);

  save(headers, rows, fileName);
};

/**
 * The blanks the run refused to bill for. Exported for the same reason they are listed:
 * a count cannot be argued with, a ticket number can be looked up.
 */
export const exportSkippedTickets = (
  skipped: SkippedTicket[],
  fileName: string,
  grouped = false,
) => {
  const headers = [
    ...(grouped ? ["Stream"] : []),
    "Date",
    "Ticket #",
    "Why it was skipped",
  ];
  const rows = skipped.map((ticket) => [
    ...(grouped ? [royaltyStreamLabel(ticket.stream)] : []),
    ticket.date ? formatDate(ticket.date, false) : "",
    `${ticket.prefix}-${ticket.ticketNumber}`,
    SkippedTicketReason,
  ]);

  save(headers, rows, fileName);
};
